import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

function CounterValue({ value }) {
  // Animate numeric portion if any
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const num = parseFloat(String(value).replace(/[^\d.]/g, ""));
    if (!isFinite(num) || num === 0) {
      setDisplay(value);
      return;
    }
    let started = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          const duration = 1200;
          const start = performance.now();
          const prefix = String(value).match(/^[^\d]+/)?.[0] || "";
          const suffix = String(value).match(/[^\d.]+$/)?.[0] || "";
          const isFloat = String(value).includes(".");
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const cur = num * eased;
            setDisplay(`${prefix}${isFloat ? cur.toFixed(1) : Math.round(cur)}${suffix}`);
            if (t < 1) requestAnimationFrame(tick);
            else setDisplay(value);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  return <span ref={ref}>{display}</span>;
}

export default function Stats({ data }) {
  return (
    <section data-testid="stats-section" className="py-20 lg:py-24 bg-white border-y border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {(data.overline || data.title) && (
          <div className="text-center mb-12">
            {data.overline && <p className="overline mb-3" data-testid="stats-overline">{data.overline}</p>}
            {data.title && <h2 data-testid="stats-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-onyx">{data.title}</h2>}
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {(data.items || []).map((it, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`stat-item-${i}`}
              className="text-center"
            >
              <p className="font-serif text-5xl sm:text-6xl text-onyx font-light">
                <CounterValue value={it.value} />
              </p>
              <p className="mt-2 text-xs tracking-[0.2em] uppercase text-onyx/60">{it.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
